/**
 * The master pane's rows: the scroller, the keyboard path down the list, and the copy each row
 * wears — for both scopes.
 *
 * ── One scroller, one keyboard path, two row shapes ──────────────────────────────────────────
 * The switch above the list swaps what a row IS, not how the list behaves: the arrows walk it the
 * same way, `Enter` follows the same anchor, and the selection is remembered by the same hook. So
 * the branch is one `switch` over the discriminated list and everything around it is written once.
 *
 * ── The arrows move FOCUS, and focus moves the selection ─────────────────────────────────────
 * One mechanism, not two. `usePointedRow` answers which row is next; this handler focuses that
 * row's link; the focus lands on the row's `onFocusCapture`, which points at it. So a keyboard user
 * and a pointer user drive the same state, and there is no second set of controls for the list.
 *
 * The handler sits on the SCROLLER rather than on the list, so it is heard whether focus is on the
 * region itself — which is where `Tab` lands first — or on a row's link inside it.
 *
 * ── A row's KEY is namespaced by its scope, and that is not decoration ───────────────────────
 * A template's id and an assignment's id are two counters over two tables, so `41` can be both. The
 * selection is remembered by key across a scope switch (see `usePointedRow`), and an unprefixed
 * collision would carry the trainer's choice from one list into a coincidentally-numbered row of the
 * other — rare, silent, and impossible to reproduce on demand.
 *
 * ── The copy is composed HERE, from the row's facts ──────────────────────────────────────────
 * `model/` answers what a plan IS: a span of eight weeks or a loop, twelve of thirty workouts done.
 * Turning that into a sentence is pluralisation, which is `t`'s job at the call site — `model/`
 * takes a key-only translate and cannot pass a count.
 *
 * ── A template's sub-line IS its span, and nothing else ──────────────────────────────────────
 * Owner decision **D26**, 4 September 2026. It used to read `8 tygodni · 42 ćwiczenia`; the count
 * is now the inspector hero's FIRST KPI beside the row, so the row was printing a figure the pane
 * repeats a hundred pixels away and paying for it with the only line it has for a plan's shape.
 * `PlanSpan` in `model/planLibraryRow` is that decision — the projection answers with the span and
 * with nothing to concatenate to it, and this file no longer joins anything. `row.exerciseCount`
 * is still on the row, because the hero reads it.
 *
 * A plan with no phases has no span, so it has no sub-line: `null`, and the name stands alone.
 * `0 tygodni` would be a figure where the truth is an absence.
 *
 * ── There is deliberately NO live region announcing the selection ────────────────────────────
 * The selection follows the pointer, so a sweep down eight rows would fire eight announcements and
 * a reader would hear the list rather than the plan. What the selection is FOR is the inspector
 * beside it, and the inspector carries the plan's name as its own heading — which is the place a
 * listener reaches deliberately.
 *
 * **The count is pluralised properly, and the specimen is not.** KANON writes `42 ćwiczeń`, which
 * is the genitive plural under a number Polish puts in the nominative plural: 42 is `ćwiczenia`.
 * The mock builds the string by concatenation and has no plural rule; this app has `_one/_few/_many`
 * and uses them. Grammatical number is the one correction this repo makes to copy without asking.
 */

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import type { ClientPlanRow as ClientPlanRowModel } from '../model/clientPlanRow';
import type { PlanLibraryList } from '../lib/usePlanLibrary';
import { personName } from '../model/templateFollowers';
import type { PointedRow } from '~/shared/lib/usePointedRow';
import { ClientPlanRow } from './ClientPlanRow';
import { spanLabel } from './spanLabel';
import { TemplateRow } from './TemplateRow';
import { usePhotoReveal } from '~/shared/lib/usePhotoReveal';
import scrollbar from '~/shared/ui/scrollbar.module.css';
import styles from './ListRows.module.css';

interface ListRowsProps {
  readonly list: PlanLibraryList;
  readonly pointer: PointedRow;
  /** `useSkeletonGate`'s hand-over class, for the rows that replace the list skeleton. */
  readonly className?: string;
}

/** `t:41` for a template, `c:4107` for an assignment. See the header for why the prefix is load-bearing. */
export function templateRowKey(id: number): string {
  return `t:${String(id)}`;
}

export function clientRowKey(assignmentId: number): string {
  return `c:${String(assignmentId)}`;
}

/**
 * `Kategoria: Siła` for a row's accessible name, or `null` where the plan carries no category.
 *
 * The category reaches the screen as a 2.5px coloured rule under the cover and as nothing else, so
 * a reader user meets a facet in the filter panel they cannot hear on a row. One sentence, composed
 * once for both row shapes.
 */
function categorySpoken(category: string | null, t: TFunction): string | null {
  return category === null ? null : t('web:planLibrary.row.categorySpoken', { name: category });
}

/** `Wstrzymany` where a plan is paused, `null` where it is live — D7's word, which the row keeps. */
function pausedLabel(row: ClientPlanRowModel, t: TFunction): string | null {
  return row.status === 'paused' ? t('web:planLibrary.clientPlans.paused') : null;
}

/**
 * How many rows a screenful is, for `PageUp` and `PageDown`.
 *
 * Measured rather than assumed: the two scopes draw rows at different heights (73px for a template,
 * 86px for a client's plan) and the card's own height is whatever the viewport left it. One row is
 * the floor, so a pane too short to hold a whole row still moves.
 */
function visibleRows(scroller: HTMLElement): number {
  const row = scroller.querySelector('li');
  const height = row?.getBoundingClientRect().height ?? 0;
  if (height <= 0) return 1;
  return Math.max(1, Math.floor(scroller.clientHeight / height));
}

/**
 * How far a key moves the selection, or `null` for a key this list does not answer.
 *
 * `page` is a thunk because measuring the rows costs a layout read, and four of the six keys here
 * never need it.
 */
function keyboardSteps(key: string, page: () => number): number | null {
  switch (key) {
    case 'ArrowDown':
      return 1;
    case 'ArrowUp':
      return -1;
    case 'PageDown':
      return page();
    case 'PageUp':
      return -page();
    // Clamped by `step`, so the far end is whatever the list's length happens to be.
    case 'End':
      return Infinity;
    case 'Home':
      return -Infinity;
    default:
      return null;
  }
}

export function ListRows({ list, pointer, className }: ListRowsProps) {
  const { t } = useTranslation();
  /* The covers of this list are ONE group: they are requested in the same frame off the same
   * connection, so they appear on one frame rather than dripping in as each file lands. Every row
   * carries the same two classes and reports its own arrival — see `shared/lib/usePhotoReveal`.
   * Each row draws exactly one cover, and `thumbUrl` is never absent (the projection falls back to
   * a generated one), so the row count IS the photograph count.
   *
   * The SCOPE is the group's identity, because one `ListRows` element serves both of them: the
   * switch to `Plany klientów` replaces every cover in the list, and a window that closed for the
   * templates would let the new set drip in exactly as it used to. */
  const photo = usePhotoReveal({ photoCount: list.rows.length, groupKey: list.scope });

  return (
    <div
      className={`${String(scrollbar.scrollbar)} ${String(styles.scroller)} ${className ?? ''}`}
      role="region"
      tabIndex={0}
      aria-label={t('web:planLibrary.scrollRegion')}
      onKeyDown={(event) => {
        const scroller = event.currentTarget;
        // Focus on the REGION's own tab stop rather than on a row. The first press then ENTERS the
        // list at the row the pane is already describing: an arrow computed from the selection
        // regardless of where focus was skipped row one entirely, so a trainer who tabbed in and
        // pressed Down watched the pane jump to a plan they had not chosen.
        const entering = event.target === scroller;

        if (event.key === 'Escape') {
          // Out of the rows and back onto the region, which is one shift-Tab from the toolbar
          // above. A trainer thirty rows down had no way back to the list's own control.
          if (entering) return;
          event.preventDefault();
          scroller.focus();
          return;
        }

        const steps = keyboardSteps(event.key, () => visibleRows(scroller));
        if (steps === null) return;
        // Entering, every RELATIVE key lands on the selection; `Home` and `End` are absolute and
        // mean the same thing wherever focus was, which is what `Infinity` says here.
        const next = pointer.step(entering && Number.isFinite(steps) ? 0 : steps);
        if (next === null) return;
        const link = scroller.querySelector<HTMLAnchorElement>(`[data-plan-row="${next}"]`);
        // Already there — at either end, or entering onto the row that has focus. The event is left
        // alone so the browser's own scroll still works.
        if (link === null || link === document.activeElement) return;
        event.preventDefault();
        link.focus();
      }}
    >
      <ul className={styles.list}>
        {list.scope === 'templates'
          ? list.rows.map((row) => {
              const key = templateRowKey(row.id);
              return (
                <TemplateRow
                  key={key}
                  rowKey={key}
                  row={row}
                  subline={spanLabel(row.span, t)}
                  categorySpoken={categorySpoken(row.category, t)}
                  selected={pointer.selectedKey === key}
                  onPoint={() => {
                    pointer.point(key);
                  }}
                  photo={photo}
                />
              );
            })
          : list.rows.map((row) => {
              const key = clientRowKey(row.assignmentId);
              return (
                <ClientPlanRow
                  key={key}
                  rowKey={key}
                  row={row}
                  // A client the backend composed no name for still gets a link with a name, which
                  // is the same fallback the strip and the Klient facet already draw.
                  name={personName(row.client.fullName, t)}
                  categorySpoken={categorySpoken(row.category, t)}
                  pausedLabel={pausedLabel(row, t)}
                  percentLabel={t('web:planLibrary.clientPlans.percent', {
                    percent: row.progressPct,
                  })}
                  // The bar's width and the figure beside it are one fact drawn twice, and neither
                  // is readable on its own: a width has no value and `40%` has no subject. A paused
                  // plan says so here too, because the column that carries that word is hidden.
                  progressSpoken={t(
                    row.status === 'paused'
                      ? 'web:planLibrary.clientPlans.progressSpokenPaused'
                      : 'web:planLibrary.clientPlans.progressSpoken',
                    {
                      count: row.workoutsTotal,
                      done: row.workoutsDone,
                      percent: row.progressPct,
                    },
                  )}
                  selected={pointer.selectedKey === key}
                  onPoint={() => {
                    pointer.point(key);
                  }}
                  photo={photo}
                />
              );
            })}
      </ul>
    </div>
  );
}
